// src/pages/Expenses/parts/ViewExpenseModal.tsx
//
// Expense detail view — GET /expenses/:id, with approve
// (PUT /expenses/:id/approve) and reject (PUT /expenses/:id/reject, JSON
// body {reason}) available while the expense is still pending.

import React, { useEffect, useRef, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { ProgressSpinner } from 'primereact/progressspinner';
import apiClient from '../../../stateManagers/services/axiosInterceptor';
import { GlobalButton } from '../../../components/GlobalComponents/GlobalButton';
import { GlobalTextArea } from '../../../components/GlobalComponents/GlobalTextArea';
import {
  ApiExpense,
  ExpenseRecord,
  STATUS_META,
  formatCurrency,
  formatDateTime,
  formatExpenseDate,
  isReceiptImage,
  mapApiExpense,
  paymentModeLabel,
} from './types';

interface ViewExpenseModalProps {
  open: boolean;
  expenseId: string | null;
  onClose: () => void;
  onChanged: () => void;
  onEdit: (expense: ExpenseRecord) => void;
}

const ViewExpenseModal: React.FC<ViewExpenseModalProps> = ({
  open,
  expenseId,
  onClose,
  onChanged,
  onEdit,
}) => {
  const toast = useRef<Toast>(null);
  const [expense, setExpense] = useState<ExpenseRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [showRejectBox, setShowRejectBox] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [isClosing, setIsClosing] = useState(false);

  const busy = approving || rejecting;

  useEffect(() => {
    if (!open || !expenseId) {
      setExpense(null);
      setIsClosing(false);
      return;
    }

    let cancelled = false;
    setShowRejectBox(false);
    setRejectReason('');

    const fetchExpense = async () => {
      setLoading(true);
      try {
        const res = await apiClient.get(`api/expenses/${expenseId}`);
        if (cancelled) return;
        if (res.data.status === 'success') {
          setExpense(mapApiExpense(res.data.data as ApiExpense));
        } else {
          toast.current?.show({
            severity: 'error',
            summary: 'Not loaded',
            detail: res.data.message || 'This expense could not be loaded.',
            life: 5000,
          });
        }
      } catch (err: any) {
        if (cancelled) return;
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: err.response?.data?.message || 'Could not load this expense.',
          life: 5000,
        });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchExpense();
    return () => {
      cancelled = true;
    };
  }, [open, expenseId]);

  useEffect(() => {
    if (isClosing) {
      const dialog = document.querySelector('.p-dialog');
      const mask = document.querySelector('.p-dialog-mask');
      if (dialog) dialog.classList.add('dialog-closing');
      if (mask) mask.classList.add('mask-closing');
    }
  }, [isClosing]);

  const handleClose = () => {
    if (busy) return;
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, 400);
  };

  const handleApprove = async () => {
    if (!expense || approving) return;
    setApproving(true);
    try {
      const res = await apiClient.put(`api/expenses/${expense.id}/approve`);
      if (res.data.status === 'success') {
        setExpense({ ...expense, status: 'approved' });
        toast.current?.show({
          severity: 'success',
          summary: 'Approved',
          detail: `${formatCurrency(expense.amount)} for ${expense.payeeName} approved.`,
          life: 4000,
        });
        onChanged();
      } else {
        toast.current?.show({
          severity: 'error',
          summary: 'Failed',
          detail: res.data.message || 'The expense could not be approved.',
          life: 5000,
        });
      }
    } catch (err: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err.response?.data?.message || 'Something went wrong while approving.',
        life: 5000,
      });
    } finally {
      setApproving(false);
    }
  };

  const handleReject = async () => {
    if (!expense || rejecting) return;
    if (!rejectReason.trim()) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Reason required',
        detail: 'Add a reason before rejecting this expense.',
        life: 4000,
      });
      return;
    }
    setRejecting(true);
    try {
      const res = await apiClient.put(`api/expenses/${expense.id}/reject`, {
        reason: rejectReason.trim(),
      });
      if (res.data.status === 'success') {
        setExpense({
          ...expense,
          status: 'rejected',
          rejectionReason: rejectReason.trim(),
        });
        setShowRejectBox(false);
        toast.current?.show({
          severity: 'success',
          summary: 'Rejected',
          detail: `${expense.payeeName}'s expense was rejected.`,
          life: 4000,
        });
        onChanged();
      } else {
        toast.current?.show({
          severity: 'error',
          summary: 'Failed',
          detail: res.data.message || 'The expense could not be rejected.',
          life: 5000,
        });
      }
    } catch (err: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err.response?.data?.message || 'Something went wrong while rejecting.',
        life: 5000,
      });
    } finally {
      setRejecting(false);
    }
  };

  const sectionCardStyle: React.CSSProperties = {
    background: 'rgba(255, 255, 255, 0.8)',
    borderRadius: 16,
    padding: 20,
    border: '1px solid rgba(59, 130, 246, 0.1)',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.05)',
  };

  const Row: React.FC<{ icon: string; label: string; value: React.ReactNode }> = ({
    icon,
    label,
    value,
  }) => (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        padding: '10px 12px',
        borderRadius: 12,
        background: 'rgba(248, 250, 252, 0.9)',
        border: '1px solid rgba(226, 232, 240, 0.8)',
      }}
    >
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: 10,
          background:
            'linear-gradient(135deg, rgba(30, 58, 138, 0.1) 0%, rgba(59, 130, 246, 0.12) 100%)',
          color: '#3b82f6',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <i className={icon} style={{ fontSize: 14 }} />
      </div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: '#64748b',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            marginBottom: 4,
          }}
        >
          {label}
        </div>
        <div
          style={{
            fontSize: 14,
            fontWeight: 600,
            color: '#1e293b',
            lineHeight: 1.35,
            wordBreak: 'break-word',
          }}
        >
          {value || <span style={{ color: '#94a3b8', fontWeight: 500 }}>—</span>}
        </div>
      </div>
    </div>
  );

  const Section: React.FC<{ icon: string; title: string; children: React.ReactNode }> = ({
    icon,
    title,
    children,
  }) => (
    <div style={sectionCardStyle}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          marginBottom: 16,
          paddingBottom: 12,
          borderBottom: '1px solid rgba(229, 231, 235, 0.6)',
        }}
      >
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: 8,
            background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <i className={icon} style={{ fontSize: 14 }} />
        </div>
        <span
          style={{
            color: '#1e3a8a',
            fontWeight: 700,
            fontSize: 14,
            letterSpacing: '0.02em',
            textTransform: 'uppercase',
          }}
        >
          {title}
        </span>
      </div>
      {children}
    </div>
  );

  const statusMeta = expense ? STATUS_META[expense.status] : null;
  const isPending = expense?.status === 'pending';

  const headerTemplate = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '4px 0', width: '100%' }}>
      <div
        style={{
          width: 54,
          height: 54,
          borderRadius: 14,
          background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
          color: 'white',
          fontSize: 20,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          boxShadow: '0 8px 24px rgba(59, 130, 246, 0.3)',
          border: '3px solid white',
        }}
      >
        <i className="pi pi-receipt" />
      </div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div
          style={{
            fontSize: 20,
            fontWeight: 700,
            color: '#1e40af',
            lineHeight: 1.2,
            marginBottom: 4,
          }}
        >
          {loading
            ? 'Loading expense…'
            : expense
              ? formatCurrency(expense.amount)
              : 'Expense details'}
        </div>
        <div style={{ fontSize: 13, color: '#64748b', fontWeight: 500 }}>
          {loading
            ? 'Fetching expense details'
            : expense?.payeeName
              ? `${expense.payeeName} · ${expense.category}`
              : 'Expense record'}
        </div>
      </div>
      {statusMeta && !loading && (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '7px 14px',
            borderRadius: 999,
            fontSize: 12,
            fontWeight: 700,
            color: statusMeta.color,
            background: statusMeta.bg,
            border: `1px solid ${statusMeta.border}`,
            flexShrink: 0,
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
          }}
        >
          <i className={statusMeta.icon} style={{ fontSize: 12 }} />
          {statusMeta.label}
        </span>
      )}
    </div>
  );

  return (
    <>
      <Toast ref={toast} position="top-right" />

      <Dialog
        visible={open && !isClosing}
        onHide={handleClose}
        header={headerTemplate}
        style={{ width: '90vw', maxWidth: 920, borderRadius: 24, overflow: 'hidden' }}
        contentStyle={{
          padding: 18,
          background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
          maxHeight: 'calc(90vh - 140px)',
          overflowY: 'auto',
          border: '12px solid rgba(255, 255, 255, 0.8)',
          borderRadius: '0 0 24px 24px',
          borderTop: 'none',
        }}
        headerStyle={{
          background: 'white',
          borderBottom: '1px solid #e5e7eb',
          padding: '20px 24px',
          borderRadius: '24px 24px 0 0',
          borderLeft: '12px solid rgba(255, 255, 255, 0.8)',
          borderRight: '12px solid rgba(255, 255, 255, 0.8)',
          borderTop: '12px solid rgba(255, 255, 255, 0.8)',
          margin: '-12px -12px 0 -12px',
        }}
        modal
        dismissableMask={!busy}
        draggable={false}
        resizable={false}
        blockScroll
      >
        <style>{`
          .p-dialog-mask {
            backdrop-filter: blur(4px);
            background-color: rgba(0, 0, 0, 0.25) !important;
            animation: maskFadeIn 0.35s ease;
          }
          .p-dialog-mask.mask-closing {
            animation: maskFadeOut 0.4s ease !important;
          }
          @keyframes maskFadeIn {
            from { opacity: 0; background-color: rgba(0, 0, 0, 0); }
            to { opacity: 1; background-color: rgba(0, 0, 0, 0.25); }
          }
          @keyframes maskFadeOut {
            from { opacity: 1; background-color: rgba(0, 0, 0, 0.25); }
            to { opacity: 0; background-color: rgba(0, 0, 0, 0); }
          }
          .p-dialog {
            animation: dialogOpen 0.35s cubic-bezier(0.4, 0, 0.2, 1);
            box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25) !important;
          }
          .p-dialog.dialog-closing {
            animation: dialogClose 0.4s cubic-bezier(0.4, 0, 0.2, 1) !important;
          }
          @keyframes dialogOpen {
            from { opacity: 0; transform: scale(0.92) translateY(-20px); }
            to { opacity: 1; transform: scale(1) translateY(0); }
          }
          @keyframes dialogClose {
            from { opacity: 1; transform: scale(1) translateY(0); }
            to { opacity: 0; transform: scale(0.88) translateY(25px); }
          }
          .p-dialog .p-dialog-content::-webkit-scrollbar { width: 8px; }
          .p-dialog .p-dialog-content::-webkit-scrollbar-track {
            background: rgba(241, 245, 249, 0.5);
            border-radius: 10px;
          }
          .p-dialog .p-dialog-content::-webkit-scrollbar-thumb {
            background: rgba(148, 163, 184, 0.5);
            border-radius: 10px;
          }
          .p-dialog .p-dialog-content::-webkit-scrollbar-thumb:hover {
            background: rgba(100, 116, 139, 0.7);
          }
        `}</style>

        {loading ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '64px 20px',
              gap: 16,
            }}
          >
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: 16,
                background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 8px 24px rgba(59, 130, 246, 0.3)',
              }}
            >
              <ProgressSpinner
                style={{ width: 28, height: 28 }}
                strokeWidth="4"
                animationDuration=".8s"
              />
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ color: '#1e40af', fontSize: 15, fontWeight: 700, marginBottom: 4 }}>
                Loading expense
              </div>
              <div style={{ color: '#64748b', fontSize: 13, fontWeight: 500 }}>
                Fetching expense details…
              </div>
            </div>
          </div>
        ) : expense ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Reject reason input */}
            {showRejectBox && isPending && (
              <div
                style={{
                  background: 'rgba(254, 242, 242, 0.95)',
                  border: '1px solid rgba(252, 165, 165, 0.8)',
                  borderRadius: 16,
                  padding: 18,
                  boxShadow: '0 4px 16px rgba(239, 68, 68, 0.08)',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    marginBottom: 12,
                  }}
                >
                  <i
                    className="pi pi-exclamation-triangle"
                    style={{ color: '#b91c1c', fontSize: 15 }}
                  />
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#b91c1c' }}>
                    Reason for rejection
                  </span>
                </div>
                <GlobalTextArea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  rows={2}
                  autoResize
                  placeholder="Explain why this expense is being rejected…"
                  disabled={rejecting}
                />
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 8,
                    marginTop: 12,
                    flexWrap: 'wrap',
                  }}
                >
                  <GlobalButton
                    label="Cancel"
                    onClick={() => setShowRejectBox(false)}
                    variant="secondary"
                    size="medium"
                    disabled={rejecting}
                    fullWidth={false}
                  />
                  <GlobalButton
                    label={rejecting ? 'Rejecting…' : 'Confirm rejection'}
                    icon="pi pi-times"
                    onClick={handleReject}
                    variant="primary"
                    size="medium"
                    disabled={rejecting}
                    loading={rejecting}
                    fullWidth={false}
                  />
                </div>
              </div>
            )}

            {/* Already rejected banner */}
            {expense.status === 'rejected' && expense.rejectionReason && (
              <div
                style={{
                  background: 'rgba(254, 242, 242, 0.95)',
                  border: '1px solid rgba(252, 165, 165, 0.8)',
                  borderRadius: 16,
                  padding: '14px 16px',
                  display: 'flex',
                  gap: 12,
                  alignItems: 'flex-start',
                }}
              >
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 8,
                    background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <i className="pi pi-times-circle" style={{ fontSize: 14 }} />
                </div>
                <div>
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: '#b91c1c',
                      marginBottom: 4,
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                    }}
                  >
                    Rejected
                  </div>
                  <div style={{ fontSize: 13, color: '#991b1b', lineHeight: 1.5, fontWeight: 500 }}>
                    {expense.rejectionReason}
                  </div>
                </div>
              </div>
            )}

            {/* Amount highlight */}
            <div
              style={{
                background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
                borderRadius: 16,
                padding: '18px 22px',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 16,
                boxShadow: '0 8px 24px rgba(59, 130, 246, 0.3)',
                border: '3px solid rgba(255,255,255,0.15)',
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    opacity: 0.85,
                    marginBottom: 6,
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                  }}
                >
                  Total amount
                </div>
                <div style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-0.02em' }}>
                  {formatCurrency(expense.amount)}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 12, fontWeight: 600, opacity: 0.85, marginBottom: 4 }}>
                  {formatExpenseDate(expense.expenseDate)}
                </div>
                <div style={{ fontSize: 13, fontWeight: 700, opacity: 0.95 }}>
                  {expense.category}
                </div>
              </div>
            </div>

            {/* Expense details */}
            <Section icon="pi pi-info-circle" title="Expense details">
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                  gap: 10,
                }}
              >
                <Row icon="pi pi-tag" label="Category" value={expense.category} />
                <Row
                  icon="pi pi-calendar"
                  label="Expense date"
                  value={formatExpenseDate(expense.expenseDate)}
                />
              </div>
              {expense.description ? (
                <div
                  style={{
                    marginTop: 12,
                    padding: '12px 14px',
                    borderRadius: 12,
                    background: 'rgba(248, 250, 252, 0.9)',
                    border: '1px solid rgba(226, 232, 240, 0.8)',
                    fontSize: 14,
                    color: '#475569',
                    lineHeight: 1.55,
                    fontWeight: 500,
                  }}
                >
                  {expense.description}
                </div>
              ) : (
                <div
                  style={{
                    marginTop: 10,
                    fontSize: 13,
                    color: '#94a3b8',
                    fontWeight: 500,
                  }}
                >
                  No description recorded.
                </div>
              )}
            </Section>

            {/* Payee */}
            <Section icon="pi pi-user" title="Payee">
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                  gap: 10,
                }}
              >
                <Row icon="pi pi-id-card" label="Name" value={expense.payeeName} />
                <Row icon="pi pi-phone" label="Contact" value={expense.payeeContact} />
              </div>
            </Section>

            {/* Payment */}
            <Section icon="pi pi-credit-card" title="Payment">
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                  gap: 10,
                }}
              >
                <Row
                  icon="pi pi-wallet"
                  label="Payment mode"
                  value={paymentModeLabel(expense.paymentMode)}
                />
                <Row
                  icon="pi pi-hashtag"
                  label="Transaction ref"
                  value={expense.transactionRef}
                />
              </div>
            </Section>

            {/* Receipt */}
            <Section icon="pi pi-paperclip" title="Receipt">
              {expense.receiptUrl ? (
                isReceiptImage(expense.receiptUrl) ? (
                  <a
                    href={expense.receiptUrl}
                    target="_blank"
                    rel="noreferrer"
                    style={{ display: 'inline-block', textDecoration: 'none' }}
                  >
                    <div
                      style={{
                        borderRadius: 14,
                        overflow: 'hidden',
                        border: '1.5px solid rgba(59, 130, 246, 0.2)',
                        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.06)',
                        maxWidth: 360,
                        background: '#f8fafc',
                      }}
                    >
                      <img
                        src={expense.receiptUrl}
                        alt="Receipt"
                        style={{
                          width: '100%',
                          display: 'block',
                          maxHeight: 220,
                          objectFit: 'cover',
                        }}
                      />
                      <div
                        style={{
                          padding: '8px 12px',
                          fontSize: 12,
                          fontWeight: 600,
                          color: '#3b82f6',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                          background: 'rgba(239, 246, 255, 0.9)',
                        }}
                      >
                        <i className="pi pi-external-link" style={{ fontSize: 11 }} />
                        Open full size
                      </div>
                    </div>
                  </a>
                ) : (
                  <GlobalButton
                    label="Open receipt PDF"
                    icon="pi pi-file-pdf"
                    onClick={() => window.open(expense.receiptUrl!, '_blank', 'noreferrer')}
                    variant="secondary"
                    size="medium"
                    fullWidth={false}
                  />
                )
              ) : (
                <div
                  style={{
                    padding: '14px 16px',
                    borderRadius: 12,
                    border: '1.5px dashed #c7d2fe',
                    background: 'linear-gradient(135deg, #eff6ff 0%, #f8fafc 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    color: '#64748b',
                    fontSize: 13,
                    fontWeight: 500,
                  }}
                >
                  <i className="pi pi-inbox" style={{ fontSize: 16, color: '#93c5fd' }} />
                  No receipt was attached to this expense.
                </div>
              )}
            </Section>

            {/* Activity */}
            <Section icon="pi pi-history" title="Activity">
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                  gap: 10,
                }}
              >
                <Row icon="pi pi-user-plus" label="Recorded by" value={expense.recordedByName} />
                <Row icon="pi pi-verified" label="Reviewed by" value={expense.approvedByName} />
                <Row
                  icon="pi pi-clock"
                  label="Recorded on"
                  value={formatDateTime(expense.createdAt)}
                />
              </div>
            </Section>

            {/* Actions */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                flexWrap: 'wrap',
                gap: 10,
                marginTop: 4,
              }}
            >
              <GlobalButton
                label="Edit details"
                icon="pi pi-pencil"
                onClick={() => onEdit(expense)}
                variant="secondary"
                size="medium"
                disabled={busy}
                fullWidth={false}
              />
              {isPending && (
                <>
                  <GlobalButton
                    label={showRejectBox ? 'Hide reject' : 'Reject'}
                    icon="pi pi-times"
                    onClick={() => setShowRejectBox((v) => !v)}
                    variant="secondary"
                    size="medium"
                    disabled={busy}
                    fullWidth={false}
                  />
                  <GlobalButton
                    label={approving ? 'Approving…' : 'Approve'}
                    icon="pi pi-check"
                    onClick={handleApprove}
                    variant="primary"
                    size="medium"
                    disabled={busy}
                    loading={approving}
                    fullWidth={false}
                  />
                </>
              )}
              <GlobalButton
                label="Close"
                onClick={handleClose}
                variant="secondary"
                size="medium"
                disabled={busy}
                fullWidth={false}
              />
            </div>
          </div>
        ) : (
          <div
            style={{
              padding: '56px 24px',
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 12,
            }}
          >
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: 16,
                background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 8px 20px rgba(239, 68, 68, 0.25)',
              }}
            >
              <i className="pi pi-exclamation-circle" style={{ fontSize: 22, color: 'white' }} />
            </div>
            <div style={{ fontWeight: 700, fontSize: 16, color: '#1e293b' }}>
              Expense unavailable
            </div>
            <div style={{ fontSize: 13, color: '#64748b', fontWeight: 500, maxWidth: 280 }}>
              This expense could not be loaded. It may have been deleted or you don’t have access.
            </div>
            <div style={{ marginTop: 8 }}>
              <GlobalButton
                label="Close"
                onClick={handleClose}
                variant="secondary"
                size="medium"
                fullWidth={false}
              />
            </div>
          </div>
        )}
      </Dialog>
    </>
  );
};

export default ViewExpenseModal;